import compareVersion from "compare-versions";
import { difference, isEqual } from "es-toolkit";

import type {
  DesignerNodeData,
  GetNextId,
  NodeDesignsState,
  SlotId,
  StateAction,
  TriggerDesign,
} from "@skilitics/nautilus-core-designer-data";
import {
  getConditionBuildCreate,
  getDesignerNodeDataByIdCreate,
  getTriggerDesignsCreate,
  queryEffectBuildsCreate,
  traverseNodeSubtree,
} from "@skilitics/nautilus-core-designer-data";

import { findParentlessNodeId } from "../../state/index.ts";
import type { GetManifestByTypeId, GetMigrateFromManifestByTypeId, GetPlaceholderManifest } from "../../types/index.ts";
import { createNodeDataCreate, createPlaceholderDataCreate } from "../creation/index.ts";
import { addTriggerActions, diffTriggerDesigns } from "./diffTriggerDesigns.ts";
import { migrateNodeCreate } from "./migrateNode.ts";

interface CreateOptions {
  getManifest: GetManifestByTypeId;
  getMigrateFromManifest: GetMigrateFromManifestByTypeId;
  getPlaceholderManifest: GetPlaceholderManifest;
  rootNodeParentContext: Record<string, unknown>;
  disableSchemaValidation?: boolean;
}

const extractIds = (node: DesignerNodeData): number[] => {
  const ids: number[] = [];
  traverseNodeSubtree(node, ({ id }) => ids.push(id));
  return ids;
};

const getRemovedIds = (fromNode: DesignerNodeData, toNode: DesignerNodeData) => {
  const fromIds = extractIds(fromNode).slice(1);
  const toIds = extractIds(toNode).slice(1);
  return difference(fromIds, toIds);
};

const serializeSlotId = ({ parentId, name }: SlotId) => `${parentId}:${name}`;

const buildSlotIdMaps = (node: DesignerNodeData) => {
  const idToNodeAndSlotMap = new Map<number, { node: DesignerNodeData; slotId: SlotId }>();
  const slotIdToNodesMap = new Map<string, { node: DesignerNodeData }[]>();

  const recurseBuildMap = (recurseNode?: DesignerNodeData, slotId?: SlotId) => {
    if (!recurseNode) return;
    if (slotId) {
      idToNodeAndSlotMap.set(recurseNode.id, { node: recurseNode, slotId });
      const sid = serializeSlotId(slotId);
      slotIdToNodesMap.set(sid, [...(slotIdToNodesMap.get(sid) ?? []), { node: recurseNode }]);
    }

    for (const [name, childOrChildren] of Object.entries(recurseNode.childNodes)) {
      const childNodes = Array.isArray(childOrChildren) ? childOrChildren : [childOrChildren];
      for (const childNode of childNodes) {
        recurseBuildMap(childNode, { parentId: recurseNode.id, name });
      }
    }
  };

  recurseBuildMap(node);

  return [idToNodeAndSlotMap, slotIdToNodesMap] as const;
};

type GetTriggerDesignsForNode = (nodeId: number) => TriggerDesign[];

const getActions = (
  fromNode: DesignerNodeData,
  toNode: DesignerNodeData,
  getTriggerDesignsForNode?: GetTriggerDesignsForNode,
) => {
  const actions: StateAction[] = [];
  const triggerRestorations: { nodeId: number; triggers: TriggerDesign[] }[] = [];

  if (fromNode.typeId !== toNode.typeId) {
    // id of 0 indicates this node is superseded and should be ignored
    if (toNode.id !== 0) {
      actions.push({
        type: "REPLACE",
        payload: { id: fromNode.id, replacement: toNode, repointRefs: true },
      });
    }
  } else {
    if (!isEqual(fromNode.config, toNode.config)) {
      if (fromNode.id === toNode.id) {
        actions.push({ type: "UPDATE_CONFIG", payload: { id: fromNode.id, config: toNode.config } });
      } else {
        actions.push({
          type: "REPLACE",
          payload: { id: fromNode.id, replacement: toNode, repointRefs: true },
        });
      }
    }

    if (isEqual(fromNode.childNodes, toNode.childNodes)) {
      return actions;
    }

    // empty arrays are a bit of a special case
    if (fromNode.id === toNode.id) {
      for (const [name, slotOrSlots] of Object.entries(toNode.childNodes)) {
        if (Array.isArray(slotOrSlots) && slotOrSlots.length === 0 && !Array.isArray(fromNode.childNodes[name])) {
          actions.push({ type: "ADD_MULTI_VALUE_SLOT", payload: { slotId: { parentId: toNode.id, name } } });
        }
      }
    }
  }

  const removeAction = ({ id }: { id: number }): StateAction => ({ type: "REMOVE", payload: { id } });

  const removedChildIds = getRemovedIds(fromNode, toNode);
  for (const id of removedChildIds) {
    actions.push(removeAction({ id }));
  }

  const [fromSlotsByNodeIdMap, fromSlotIdToNodesMap] = buildSlotIdMaps(fromNode);
  const [toSlotsByNodeIdMap, toSlotIdToNodesMap] = buildSlotIdMaps(toNode);

  const isNew = (node: DesignerNodeData) => !toSlotsByNodeIdMap.has(node.id);

  // adding a node includes descendents and those will replace the existing ones
  // so we remove any existing ones before adding

  const removeExistingDescendents = (node: DesignerNodeData) => {
    for (const childOrChilds of Object.values(node.childNodes)) {
      const childs = Array.isArray(childOrChilds) ? childOrChilds : childOrChilds ? [childOrChilds] : [];
      for (const child of childs) {
        removeExistingDescendents(child);
      }
    }

    // tail recursion as we want to remove bottom up
    if (!isNew(node)) {
      // Track triggers for restoration after REMOVE + ADD cycle
      if (getTriggerDesignsForNode) {
        const triggers = getTriggerDesignsForNode(node.id);
        if (triggers.length > 0) {
          triggerRestorations.push({ nodeId: node.id, triggers });
        }
      }
      fromSlotsByNodeIdMap.delete(node.id);
      actions.push(removeAction(node));
    }
  };

  for (const [id, { node, slotId }] of toSlotsByNodeIdMap.entries()) {
    if (!fromSlotsByNodeIdMap.get(id)) {
      removeExistingDescendents(node);
      actions.push({ type: "ADD", payload: { node, slotId } });
    }
  }

  for (const [id, from] of fromSlotsByNodeIdMap.entries()) {
    const to = toSlotsByNodeIdMap.get(id);
    if (!to || isEqual(from.slotId, to.slotId)) continue;
    if (
      (fromSlotIdToNodesMap.get(serializeSlotId(from.slotId))?.length ?? 0) > 1 ||
      (toSlotIdToNodesMap.get(serializeSlotId(to.slotId))?.length ?? 0) > 1
    ) {
      throw new Error(`Moving arrays in migrations not supported`);
    }
    actions.push({ type: "MOVE", payload: { from: from.slotId, to: to.slotId } });
  }

  // Restore triggers on children that were removed and re-added during restructuring.
  // Deduplicate by nodeId — removeExistingDescendents can track the same node multiple times
  // when a node appears both as a descendant of a new parent and as its own entry.
  const restoredNodeIds = new Set<number>();
  for (const { nodeId, triggers } of triggerRestorations) {
    if (restoredNodeIds.has(nodeId)) continue;
    restoredNodeIds.add(nodeId);
    for (const trigger of triggers) {
      actions.push(...addTriggerActions(nodeId, trigger));
    }
  }

  return actions;
};

const buildGetTriggerDesignsForNode = (nodeDesignsState: NodeDesignsState): GetTriggerDesignsForNode => {
  const { triggerDesigns } = nodeDesignsState;
  return getTriggerDesignsCreate({
    getConditionBuild: getConditionBuildCreate({ conditionsState: triggerDesigns.conditions }),
    queryEffectBuilds: queryEffectBuildsCreate({ effectsState: triggerDesigns.effects }),
    triggerIdsState: triggerDesigns.ids,
    triggersById: triggerDesigns.triggersById,
    handlersById: triggerDesigns.handlersById,
    responsesById: triggerDesigns.responsesById,
  });
};

const hasReplace = (nodeActions: StateAction[], fromId: number): boolean =>
  nodeActions.some((a) => a.type === "REPLACE" && a.payload.id === fromId);

export const migrateNodeDesignsCreate =
  ({ getManifest, getPlaceholderManifest, getMigrateFromManifest, disableSchemaValidation }: CreateOptions) =>
  (
    nodeDesignsState: NodeDesignsState,
    { primaryLanguageCode, supportedLanguageCodes }: { primaryLanguageCode: string; supportedLanguageCodes: string[] },
  ): StateAction[] => {
    let addedNextId = 0;
    const getNextId: GetNextId = () => nodeDesignsState.nextId + addedNextId++;
    const createNode = createNodeDataCreate({ getManifest, getPlaceholderManifest, getNextId });
    const createPlaceholder = createPlaceholderDataCreate(getPlaceholderManifest, createNode);

    const migrateNode = migrateNodeCreate({
      getManifest,
      getMigrateFromManifest,
      createNode,
      createPlaceholder,
      multilingual: { primaryLanguageCode, supportedLanguageCodes },
      disableSchemaValidation,
    });
    const getNodeById = getDesignerNodeDataByIdCreate(nodeDesignsState);
    const getTriggerDesignsForNode = buildGetTriggerDesignsForNode(nodeDesignsState);

    const rootNodeId = findParentlessNodeId(nodeDesignsState);
    if (!rootNodeId) {
      console.warn("No parent-less (root) node found.");
      return [];
    }

    const actions: StateAction[] = [];
    const versionsByTypeIdMap = new Map<string, string>();

    const resolveManifest = (typeId: string) =>
      getManifest(typeId, { ignoreIfNotFound: true }) ?? getMigrateFromManifest(typeId);

    traverseNodeSubtree(getNodeById(rootNodeId), (existing, ancestors, slotName) => {
      const { typeId: currentTypeId, version: currentVersion } = resolveManifest(existing.typeId);
      if (existing.version !== currentVersion || existing.typeId !== currentTypeId) {
        if (
          existing.typeId === currentTypeId &&
          existing.version &&
          compareVersion(existing.version, currentVersion) > 0
        ) {
          const message = `Node ${existing.typeId} is version ${existing.version} while current is ${currentVersion}.\nDesigner/Node Designer out of date?`;
          if (typeof alert === "function") {
            alert(message);
          }
          throw new Error(message);
        }

        // Populate triggers on the node so the migration function can read and modify them
        const existingTriggers = getTriggerDesignsForNode(existing.id);
        const existingWithTriggers =
          existingTriggers.length > 0 ? { ...existing, triggers: existingTriggers } : existing;

        const context = { slot: { parent: { typeId: ancestors[0]?.typeId ?? "" }, name: slotName ?? "" } };
        const migrated = migrateNode(existingWithTriggers, context);
        if (!migrated) {
          if (migrated === null) {
            return;
          }
          throw new Error(`No migration provided on ${existing.typeId} for version ${existing.version ?? ""}`);
        }

        const nodeActions = getActions(existing, migrated, getTriggerDesignsForNode);
        actions.push(...nodeActions);

        // Generate trigger migration actions
        // REPLACE carries triggers on the replacement node (processed by the reducer on add),
        // so we only need separate trigger actions for non-REPLACE scenarios.
        const migratedTriggers = migrated.triggers ?? [];
        if (!hasReplace(nodeActions, existing.id) && !isEqual(existingTriggers, migratedTriggers)) {
          actions.push(...diffTriggerDesigns(existing.id, existingTriggers, migratedTriggers));
        }

        versionsByTypeIdMap.set(migrated.typeId, migrated.version);
        if (migrated.typeId !== existing.typeId) {
          versionsByTypeIdMap.delete(existing.typeId);
        }
      }
    });

    if (versionsByTypeIdMap.size === 0) return actions;

    // A migration that changes a node's type adds the replacement at its manifest's
    // version, while the type's other instances are still on the version they were
    // stored at; the reducer holds one version per type and rejects the add. So the
    // versions the batch moves types to lead it rather than close it.
    const updateVersionsAction: Extract<StateAction, { type: "UPDATE_VERSIONS" }> = {
      type: "UPDATE_VERSIONS",
      payload: {
        versions: Array.from(versionsByTypeIdMap),
      },
    };

    return [updateVersionsAction, ...actions];
  };

export type MigrateNodeDesigns = ReturnType<typeof migrateNodeDesignsCreate>;
